// =====================================================
// Lizzi V2 — Global modals & helpers
// =====================================================
import React from 'react';
import { Icon } from './icons.jsx';
import { Avatar } from './components.jsx';
import { MockData } from './mock/index.js';
import { useData } from './data/DataProvider.jsx';
import { useAuth } from './auth/AuthContext.jsx';
import { createBid, runAnalysisFlow, fetchBid, retryAnalysis } from './data/repo.js';

const { useState, useEffect, useRef, useMemo, useCallback } = React;

const notify = (text, icon='Info') => {
  window.dispatchEvent(new CustomEvent('lizzi:toast', { detail:{ text, icon }}));
};

// ---------- Shared input field ----------
function Field({ label, type, defaultValue, value, onChange, placeholder, icon }) {
  const Ico = icon ? Icon[icon] : null;
  const [focus, setFocus] = useState(false);
  const controlled = value !== undefined;
  return (
    <label style={{display:'flex', flexDirection:'column', gap:8}}>
      <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.08em', textTransform:'uppercase', fontWeight:500}}>{label}</span>
      <div style={{
        display:'flex', alignItems:'center', gap:10, padding:'12px 14px',
        border:`1px solid ${focus ? 'var(--primary)' : 'var(--line-2)'}`,
        background:'var(--bg-1)', borderRadius:10, transition:'border 140ms',
        boxShadow: focus ? '0 0 0 3px var(--primary-soft)' : 'none',
      }}>
        {Ico && <span style={{color: focus ? 'var(--primary)' : 'var(--text-3)', transition:'color 140ms'}}><Ico size={15}/></span>}
        <input
          type={type}
          {...(controlled ? { value, onChange } : { defaultValue })}
          placeholder={placeholder}
          onFocus={()=>setFocus(true)} onBlur={()=>setFocus(false)}
          style={{flex:1, background:'transparent', border:0, fontSize:14.5, color:'var(--text-1)'}}/>
      </div>
    </label>
  );
}


// ---------- New Bid Modal ----------
function NewBidModal({ open, onClose }) {
  const { demoMode, reload } = useData();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('');
  const [entity, setEntity] = useState('');
  const [code, setCode] = useState('');
  const [lot, setLot] = useState('');
  const [company, setCompany] = useState('');
  const [region, setRegion] = useState('');
  const [deadline, setDeadline] = useState('');
  const [refUrl, setRefUrl] = useState('');
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [teamIds, setTeamIds] = useState([]);
  const fileRef = useRef(null);
  const toggleTeam = useCallback((id) => {
    setTeamIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const addFiles = (list) => {
    const arr = Array.from(list || []).filter(f => f.size <= 30 * 1024 * 1024);
    setFiles(prev => [...prev, ...arr]);
  };

  const reset = () => { setStep(1); setTitle(''); setEntity(''); setCode(''); setLot(''); setCompany(''); setRegion(''); setDeadline(''); setRefUrl(''); setFiles([]); setLeadId(null); setTeamIds([]); };

  const finish = async () => {
    if (!title.trim()) { notify('Añade un título para la licitación.', 'AlertTriangle'); setStep(1); return; }
    if (!demoMode && !refUrl.trim()) { notify('Sin enlace de la licitación el análisis será parcial (sin plazo ni datos oficiales de la web).', 'Globe'); }
    if (demoMode) {
      onClose(); reset();
      notify('Licitación creada (modo demo · no se guarda).', 'CheckCircle');
      return;
    }
    setSaving(true);
    try {
      const bid = await createBid({ title: title.trim(), entity: entity.trim(), code: code.trim(), lot: lot.trim(), company: company || null, region: region.trim() || null, deadline: deadline || null, teamLead: leadId, team: teamIds, analysisStatus: 'processing', analysisPhase: 'uploading', analysisPct: 1 }, user?.id);
      const pendingFiles = files.slice();
      const url = refUrl.trim();
      // Abrimos el overlay de progreso ANTES de cerrar el modal para que no haya
      // un salto "vacío" a la tabla: el usuario ve la transición encadenada.
      window.dispatchEvent(new CustomEvent('lizzi:analysis-open', { detail: { bidId: bid.id, title: bid.title } }));
      onClose(); reset();
      reload();
      // El overlay premium toma el relevo y muestra el progreso del análisis
      runAnalysisFlow({ bidId: bid.id, title: bid.title, files: pendingFiles, sourceUrl: url, userId: user?.id, autofillBase: false })
        .then(() => reload())
        .catch(() => {});
    } catch (e) {
      notify('No se pudo crear la licitación: ' + (e?.message || 'error'), 'AlertTriangle');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{width:560}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:'26px 28px 18px', borderBottom:'1px solid var(--line-1)'}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <div>
              <div className="page-eyebrow" style={{margin:0, marginBottom:6}}>NUEVA LICITACIÓN · PASO {step}/3</div>
              <h3 className="font-display-heavy" style={{fontSize:26, margin:0, letterSpacing:'-0.03em'}}>
                {step === 1 ? 'Información básica' : step === 2 ? 'Subir pliego' : 'Equipo y plazos'}
              </h3>
            </div>
            <button className="btn btn-ghost btn-icon" onClick={onClose}><Icon.X size={15}/></button>
          </div>
        </div>

        <div style={{padding:'24px 28px', display:'flex', flexDirection:'column', gap:18}}>
          {step === 1 && (
            <>
              <Field label="Título de la licitación" type="text" value={title} onChange={e=>setTitle(e.target.value)} placeholder="Ej. Servicios de consultoría en transformación digital"/>
              <Field label="Entidad contratante" type="text" value={entity} onChange={e=>setEntity(e.target.value)} placeholder="Ej. SANDETEL, Ayto. de Madrid, Ministerio…"/>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                <Field label="Código de expediente" type="text" value={code} onChange={e=>setCode(e.target.value)} placeholder="EXPT 2026/0000"/>
                <label style={{display:'flex', flexDirection:'column', gap:8}}>
                  <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.08em', textTransform:'uppercase', fontWeight:500}}>Lote</span>
                  <select value={lot} onChange={e=>setLot(e.target.value)} style={{padding:'12px 14px', border:'1px solid var(--line-2)', background:'var(--bg-1)', borderRadius:10, fontSize:14.5, color:'var(--text-1)'}}>
                    <option value="">Lote único</option>
                    {LOT_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </label>
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                <label style={{display:'flex', flexDirection:'column', gap:8}}>
                  <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.08em', textTransform:'uppercase', fontWeight:500}}>Compañía</span>
                  <select value={company} onChange={e=>setCompany(e.target.value)} style={{padding:'12px 14px', border:'1px solid var(--line-2)', background:'var(--bg-1)', borderRadius:10, fontSize:14.5, color:'var(--text-1)'}}>
                    <option value="">—</option>
                    {COMPANY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <Field label="Región" type="text" value={region} onChange={e=>setRegion(e.target.value)} placeholder="Región"/>
              </div>



            </>
          )}
          {step === 2 && (
            <>
              <Field label="Enlace de la licitación" type="url" value={refUrl} onChange={e=>setRefUrl(e.target.value)} placeholder="https://… perfil del contratante / plataforma" icon="Globe"/>
              <div style={{display:'flex', alignItems:'flex-start', gap:8, fontSize:11.5, color:'var(--text-4)', marginTop:-6, marginBottom:4}}>
                <Icon.Globe size={13} style={{color:'var(--primary)', flexShrink:0, marginTop:1}}/>
                <span>Muy recomendable: Lizzi abre el enlace para extraer el <strong style={{color:'var(--text-3)'}}>plazo de presentación</strong> y datos que no aparecen en los PDFs.</span>
              </div>
              <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,application/pdf" style={{display:'none'}}
                onChange={e=>{ addFiles(e.target.files); e.target.value=''; }}/>
              <div
                onClick={()=>fileRef.current?.click()}
                onDragOver={e=>{ e.preventDefault(); }}
                onDrop={e=>{ e.preventDefault(); addFiles(e.dataTransfer.files); }}
                style={{
                  border:'1.5px dashed var(--line-3)', borderRadius:14, padding:'32px 24px',
                  textAlign:'center', background:'var(--bg-2)', cursor:'pointer',
                }}>
                <Icon.Upload size={30} style={{color:'var(--primary)'}}/>
                <div style={{fontSize:14, fontWeight:500, marginTop:12}}>Arrastra los documentos aquí</div>
                <div style={{fontSize:12.5, color:'var(--text-3)', marginTop:6}}>PPT, PCAP, anexos (PDF/Word) · máx. 30 MB · clic para elegir</div>
              </div>

              {files.length > 0 && (
                <div style={{display:'flex', flexDirection:'column', gap:6}}>
                  {files.map((f,i)=>(
                    <div key={i} style={{display:'flex', alignItems:'center', gap:10, padding:'8px 12px', border:'1px solid var(--line-1)', borderRadius:10, background:'var(--bg-1)'}}>
                      <Icon.FileText size={14} style={{color:'var(--text-3)'}}/>
                      <span style={{fontSize:13, flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{f.name}</span>
                      <span style={{fontSize:11, color:'var(--text-4)'}}>{(f.size/1048576).toFixed(1)} MB</span>
                      <button className="btn btn-ghost btn-icon" onClick={(e)=>{ e.stopPropagation(); setFiles(prev=>prev.filter((_,j)=>j!==i)); }}><Icon.X size={13}/></button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{display:'flex', alignItems:'center', gap:8, fontSize:11.5, color:'var(--text-4)'}}>
                <Icon.Sparkles size={13} style={{color:'var(--primary)'}}/>
                Al crear, <strong style={{color:'var(--primary)'}}>Lizzi</strong> lee los documentos y el enlace, y genera la ficha automáticamente.
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <Field label="Plazo de presentación" type="date" value={deadline} onChange={e=>setDeadline(e.target.value)}/>

              {/* Líder de la propuesta — selección única */}
              <div>
                <div style={{display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:10}}>
                  <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.08em', textTransform:'uppercase', fontWeight:600}}>Líder de la propuesta</span>
                  <span style={{fontSize:11, color:'var(--text-4)'}}>Responsable único</span>
                </div>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10}}>
                  {window.MockData.USERS.map(u => {
                    const active = leadId === u.id;
                    return (
                      <button key={u.id} onClick={()=>setLeadId(u.id)} style={{
                        display:'flex', alignItems:'center', gap:11, textAlign:'left',
                        padding:'10px 12px', borderRadius:12, cursor:'pointer',
                        background: active ? 'var(--primary-soft)' : 'var(--bg-2)',
                        border: active ? '1.5px solid var(--primary)' : '1.5px solid var(--line-1)',
                        transition:'background 160ms, border-color 160ms',
                      }}>
                        <Avatar user={u} size={32}/>
                        <div style={{minWidth:0, flex:1}}>
                          <div style={{fontSize:13, fontWeight:600, color:'var(--text-1)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{u.name}</div>
                          <div style={{fontSize:11.5, color:'var(--text-3)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{u.title}</div>
                        </div>
                        {active && <Icon.CheckCircle size={16} style={{color:'var(--primary)', flexShrink:0}}/>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Equipo técnico — selección múltiple */}
              <div>
                <div style={{display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:10}}>
                  <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.08em', textTransform:'uppercase', fontWeight:600}}>Equipo técnico</span>
                  <span style={{fontSize:11, color:'var(--text-4)'}}>{teamIds.length} asignado{teamIds.length === 1 ? '' : 's'}</span>
                </div>
                <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                  {window.MockData.USERS.filter(u => u.id !== leadId).map(u => {
                    const active = teamIds.includes(u.id);
                    return (
                      <button key={u.id} onClick={()=>toggleTeam(u.id)} className="chip" style={{
                        cursor:'pointer', padding:'4px 12px 4px 4px',
                        background: active ? 'var(--primary-soft)' : undefined,
                        borderColor: active ? 'var(--primary)' : undefined,
                        color: active ? 'var(--text-1)' : undefined,
                      }}>
                        <Avatar user={u} size={22}/>
                        <span style={{fontWeight: active ? 600 : 500}}>{u.name.split(' ')[0]}</span>
                        {active && <Icon.Check size={13} style={{color:'var(--primary)'}}/>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

        </div>

        <div style={{padding:'16px 28px', borderTop:'1px solid var(--line-1)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div style={{display:'flex', gap:4}}>
            {[1,2,3].map(n => (
              <span key={n} style={{ width: n===step ? 22:6, height:6, borderRadius:999, background: n<=step ? 'var(--primary)':'var(--bg-3)', transition:'width 200ms'}}></span>
            ))}
          </div>
          <div style={{display:'flex', gap:8}}>
            {step > 1 && <button className="btn btn-ghost" onClick={()=>setStep(s=>s-1)}>Atrás</button>}
            {step < 3
              ? <button className="btn btn-primary" onClick={()=>setStep(s=>s+1)}>Siguiente <Icon.ArrowRight size={13}/></button>
              : <button className="btn btn-primary" disabled={saving} onClick={finish}>
                  <Icon.Sparkles size={13}/> {saving ? 'Creando…' : 'Crear y arrancar análisis'}
                </button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Upload Pliego Modal — crea la licitación desde el PDF ----------
function UploadModal({ open, onClose }) {
  const { demoMode, reload } = useData();
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [lot, setLot] = useState('');
  const [refUrl, setRefUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const addFiles = (list) => {
    const arr = Array.from(list || []).filter((f) => f.size <= 30 * 1024 * 1024);
    setFiles((prev) => [...prev, ...arr]);
  };

  const start = async () => {
    if (!files.length) { notify('Selecciona al menos un PDF.', 'AlertTriangle'); return; }
    if (demoMode) { onClose(); setFiles([]); setLot(''); setRefUrl(''); notify('Subido (modo demo · no se guarda).', 'CheckCircle'); return; }
    if (!refUrl.trim()) { notify('Sin enlace de la licitación el análisis será parcial (sin plazo ni datos oficiales de la web).', 'Globe'); }
    setSaving(true);
    try {
      const url = refUrl.trim();
      const baseName = files[0].name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
      const bid = await createBid({ title: baseName || 'Licitación sin título', lot: lot || undefined, analysisSourceUrl: url || undefined, status: 'analysis', analysisStatus: 'processing', analysisPhase: 'uploading', analysisPct: 1 }, user?.id);
      const pending = files.slice();
      // Abrimos el overlay de progreso ANTES de cerrar el modal (transición encadenada).
      window.dispatchEvent(new CustomEvent('lizzi:analysis-open', { detail: { bidId: bid.id, title: bid.title } }));
      onClose(); setFiles([]); setLot(''); setRefUrl('');
      reload();
      runAnalysisFlow({ bidId: bid.id, title: bid.title, files: pending, sourceUrl: url || null, userId: user?.id })
        .then(() => reload())
        .catch(() => {});
    } catch (e) {
      notify('No se pudo crear la licitación: ' + (e?.message || 'error'), 'AlertTriangle');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ width: 540, padding: 28 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div className="page-eyebrow" style={{ margin: 0, marginBottom: 6 }}>SUBIR PLIEGO</div>
            <h3 className="font-display-heavy" style={{ fontSize: 26, margin: 0, letterSpacing: '-0.03em' }}>Crear licitación desde el PDF</h3>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><Icon.X size={15} /></button>
        </div>

        <input ref={fileRef} type="file" accept="application/pdf" multiple style={{ display: 'none' }} onChange={(e) => addFiles(e.target.files)} />
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          style={{
            border: `1.5px dashed ${drag ? 'var(--primary)' : 'var(--line-3)'}`, borderRadius: 14, padding: '40px 24px',
            textAlign: 'center', background: drag ? 'var(--primary-soft)' : 'var(--bg-2)', cursor: 'pointer', transition: 'all 140ms',
          }}>
          <Icon.Upload size={30} style={{ color: 'var(--primary)' }} />
          <div style={{ fontSize: 14, fontWeight: 500, marginTop: 12 }}>Arrastra los PDFs o haz clic para seleccionar</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 6 }}>PPT, PCAP, anexos · máximo 30 MB por archivo</div>
        </div>

        {files.length > 0 && (
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {files.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--line-2)', borderRadius: 8, background: 'var(--bg-1)' }}>
                <Icon.FileText size={14} style={{ color: 'var(--text-3)' }} />
                <span style={{ fontSize: 12.5, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
                <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                <button className="btn btn-ghost btn-icon" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}><Icon.X size={12} /></button>
              </div>
            ))}
          </div>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          <span style={{ fontSize: 11.5, color: 'var(--text-3)', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 500 }}>Lote (opcional)</span>
          <select value={lot} onChange={(e) => setLot(e.target.value)} style={{ padding: '12px 14px', border: '1px solid var(--line-2)', background: 'var(--bg-1)', borderRadius: 10, fontSize: 14.5, color: 'var(--text-1)' }}>
            <option value="">Todos los lotes / lote único</option>
            {LOT_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>Si eliges un lote, Lizzi analizará únicamente ese lote del pliego.</span>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          <span style={{ fontSize: 11.5, color: 'var(--text-3)', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 500 }}>Enlace de la licitación</span>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Icon.Globe size={15} style={{ position: 'absolute', left: 13, color: 'var(--text-4)' }} />
            <input type="url" value={refUrl} onChange={(e) => setRefUrl(e.target.value)} placeholder="https://… perfil del contratante / plataforma"
              style={{ width: '100%', padding: '12px 14px 12px 38px', border: '1px solid var(--line-2)', background: 'var(--bg-1)', borderRadius: 10, fontSize: 14.5, color: 'var(--text-1)' }} />
          </div>
          <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>Muy recomendable: Lizzi abre el enlace para extraer el <strong style={{ color: 'var(--text-3)' }}>plazo de presentación</strong> y datos que no están en los PDFs.</span>
        </label>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, fontSize: 12.5, color: 'var(--text-3)' }}>
          <Icon.Info size={14} /> Lizzi leerá el pliego y el enlace, y completará título, entidad, plazo y la ficha automáticamente.
        </div>


        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button className="btn btn-primary" disabled={saving || !files.length} onClick={start}>
            <Icon.Sparkles size={13} /> {saving ? 'Creando…' : 'Crear y analizar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Overlay premium de progreso de análisis ----------
const ANALYSIS_STEPS = [
  { key: 'uploading', label: 'Subiendo documentos', desc: 'Enviamos los pliegos de forma segura.', icon: 'Upload' },
  { key: 'reading', label: 'Lizzi está leyendo los pliegos', desc: 'Extrae texto, tablas y datos clave de cada documento.', icon: 'FileText' },
  { key: 'analyzing', label: 'Analizando y cruzando los datos', desc: 'Relaciona criterios, solvencia, economía y riesgos.', icon: 'Sparkles' },
  { key: 'compiling', label: 'Generando la ficha (11 secciones)', desc: 'Redacta el informe ejecutivo completo.', icon: 'Layers' },
];
const PHASE_ORDER = { uploading: 0, reading: 1, analyzing: 2, compiling: 3, done: 4, error: -1 };

// Peso de cada fase sobre el progreso total (0-100). La fase "analyzing" es la
// más larga (Claude lee los PDF y razona en una sola llamada), así que ocupa la
// mayor franja y avanza de forma simulada porque la API no expone un % real.
const PHASE_TARGET = { uploading: 10, reading: 24, analyzing: 86, compiling: 97, done: 100, error: null };

function AnalysisProgressOverlay() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [bidId, setBidId] = useState(null);
  const [title, setTitle] = useState('');
  const [phase, setPhase] = useState('uploading');
  const [error, setError] = useState(null);
  const [pct, setPct] = useState(0);
  const [docInfo, setDocInfo] = useState({ done: 0, total: 0 });
  const [retrying, setRetrying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(0);
  const targetRef = useRef(0);

  useEffect(() => {
    const onOpen = (e) => {
      setBidId(e.detail?.bidId || null);
      setTitle(e.detail?.title || 'Licitación');
      setPhase('uploading'); setError(null); setOpen(true); setMinimized(false);
      setPct(0); targetRef.current = 4; setDocInfo({ done: 0, total: 0 });
      startRef.current = Date.now(); setElapsed(0);
    };
    const onProgress = (e) => {
      const d = e.detail || {};
      if (typeof d.done === 'number' || typeof d.total === 'number') {
        setDocInfo({ done: d.done || 0, total: d.total || 0 });
      }
      if (d.phase === 'error') { setError(d.message || 'error'); return; }
      if (d.phase) {
        setPhase(d.phase);
        // Si llega un % REAL desde la BD, lo usamos directamente.
        if (typeof d.pct === 'number') {
          targetRef.current = d.pct;
          setPct((p) => Math.max(p, d.pct));
        } else {
          let t = PHASE_TARGET[d.phase] ?? targetRef.current;
          if (d.phase === 'uploading' && d.total) {
            const base = 4;
            const top = PHASE_TARGET[d.phase];
            t = base + ((top - base) * ((d.done || 0) / d.total));
          }
          targetRef.current = t;
        }
        if (d.phase === 'done') setPct(100);
      }
    };
    // Cuando se abre la ficha de la licitación que estábamos siguiendo, ocultamos el indicador
    const onBidOpen = (e) => {
      setBidId((cur) => {
        if (cur && e.detail === cur) setOpen(false);
        return cur;
      });
    };
    window.addEventListener('lizzi:analysis-open', onOpen);
    window.addEventListener('lizzi:analysis-progress', onProgress);
    window.addEventListener('lizzi:open-bid', onBidOpen);
    return () => {
      window.removeEventListener('lizzi:analysis-open', onOpen);
      window.removeEventListener('lizzi:analysis-progress', onProgress);
      window.removeEventListener('lizzi:open-bid', onBidOpen);
    };
  }, []);


  // Animación suave del % hacia el objetivo de la fase actual.
  useEffect(() => {
    if (!open || error || phase === 'done') return;
    const id = setInterval(() => {
      setPct((p) => {
        const t = targetRef.current;
        if (p >= t) return p;
        // Avance más lento cuanto más cerca del objetivo (efecto "casi listo")
        const step = Math.max(0.3, (t - p) * 0.06);
        return Math.min(t, +(p + step).toFixed(1));
      });
    }, 220);
    return () => clearInterval(id);
  }, [open, error, phase]);

  // Cronómetro del análisis (se detiene al terminar o fallar).
  useEffect(() => {
    if (!open || error || phase === 'done') return;
    const id = setInterval(() => setElapsed(Math.round((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(id);
  }, [open, error, phase]);

  const fmtElapsed = (s) => {
    const m = Math.floor(s / 60);
    const ss = String(s % 60).padStart(2, '0');
    return `${m}:${ss}`;
  };

  if (!open) return null;


  const activeIdx = error ? -1 : (PHASE_ORDER[phase] ?? 0);
  const isDone = phase === 'done' && !error;
  const shownPct = error ? Math.round(pct) : isDone ? 100 : Math.round(pct);

  const openFicha = () => {
    setOpen(false);
    if (bidId) window.dispatchEvent(new CustomEvent('lizzi:open-bid', { detail: bidId }));
  };
  const retry = async () => {
    if (!bidId || retrying) return;
    setRetrying(true);
    startRef.current = Date.now();
    setElapsed(0);
    setError(null); setPhase('reading'); setPct(10); targetRef.current = PHASE_TARGET.reading;
    try {
      const res = await retryAnalysis(bidId);
      if (res?.status === 'error') setError(res.message || 'El análisis falló.');
    } catch (e) {
      setError(e?.message || 'No se pudo relanzar el análisis.');
    } finally {
      setRetrying(false);
    }
  };
  const goToFicha = () => {
    setOpen(false);
    if (bidId) window.dispatchEvent(new CustomEvent('lizzi:open-bid', { detail: bidId }));
  };

  // ---- Indicador flotante (minimizado) ----
  if (minimized) {
    const accent = error ? 'var(--sem-danger)' : isDone ? 'var(--sem-success)' : 'var(--primary)';
    return (
      <div
        className="scale-in"
        style={{
          position: 'fixed', bottom: 92, right: 22, zIndex: 95,
          width: 300, background: 'var(--bg-1)', border: `1px solid ${isDone ? 'var(--sem-success)' : 'var(--line-2)'}`,
          borderRadius: 14, boxShadow: 'var(--shadow-3)', overflow: 'hidden',
        }}
      >
        <div style={{ padding: '13px 14px', display: 'flex', alignItems: 'center', gap: 10, background: isDone ? 'var(--sem-success-soft, var(--bg-2))' : 'transparent' }}>
          <span className={isDone || error ? '' : 'spin'} style={{ flexShrink: 0 }}>
            {error ? <Icon.AlertTriangle size={17} style={{ color: accent }} />
              : isDone ? <Icon.CheckCircle size={17} style={{ color: accent }} />
              : <Icon.Sparkles size={17} style={{ color: accent }} />}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
            <div style={{ fontSize: 11, color: isDone ? 'var(--sem-success)' : 'var(--text-3)' }}>
              {error ? 'Error en el análisis' : isDone ? 'Análisis completado' : `Analizando · ${shownPct}%`}
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" style={{ flexShrink: 0 }} title="Cerrar" onClick={() => setOpen(false)}><Icon.X size={13} /></button>
        </div>

        {/* Pasos */}
        {!error && (
          <div style={{ padding: '4px 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ANALYSIS_STEPS.map((s, i) => {
              const state = i < activeIdx || isDone ? 'done' : i === activeIdx ? 'active' : 'idle';
              const Ico = Icon[s.icon];
              return (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 9, opacity: state === 'idle' ? 0.45 : 1 }}>
                  <span style={{
                    width: 18, height: 18, borderRadius: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: state === 'done' ? 'var(--sem-success-soft, var(--bg-2))' : state === 'active' ? 'var(--primary-soft)' : 'var(--bg-2)',
                    color: state === 'done' ? 'var(--sem-success)' : state === 'active' ? 'var(--primary)' : 'var(--text-4)',
                  }}>
                    {state === 'done' ? <Icon.Check size={11} /> : <Ico size={10} className={state === 'active' ? 'pulse' : ''} />}
                  </span>
                  <span style={{ fontSize: 11.5, color: state === 'idle' ? 'var(--text-4)' : 'var(--text-2)', fontWeight: state === 'active' ? 600 : 400 }}>{s.label}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Barra + acción */}
        <div style={{ height: 4, background: 'var(--bg-2)' }}>
          <div style={{ height: '100%', width: `${shownPct}%`, transition: 'width 320ms ease', background: accent }} />
        </div>
        {(isDone || error) && (
          <button className="btn btn-primary" style={{ width: '100%', borderRadius: 0, justifyContent: 'center' }} onClick={openFicha}>
            <Icon.ArrowRight size={13} /> Abrir ficha
          </button>
        )}
      </div>
    );
  }


  return (
    <div className="modal-backdrop" style={{ backdropFilter: 'blur(6px)' }}>
      <div className="modal scale-in" style={{ width: 520, padding: 0, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
        {/* Cabecera con halo de marca */}
        <div style={{ position: 'relative', padding: '30px 30px 24px', background: 'linear-gradient(160deg, var(--primary-soft), transparent)', borderBottom: '1px solid var(--line-1)' }}>
          <div style={{ position: 'absolute', top: -40, right: -20, width: 160, height: 160, borderRadius: 999, background: 'radial-gradient(circle, var(--primary-soft), transparent 70%)', pointerEvents: 'none' }} />
          {!isDone && !error && (
            <button
              className="btn btn-ghost btn-icon"
              onClick={() => setMinimized(true)}
              title="Minimizar (el análisis sigue en segundo plano)"
              style={{ position: 'absolute', top: 16, right: 16 }}
            >
              <Icon.Minus size={15} />
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 46, height: 46, borderRadius: 14, background: 'var(--bg-2)', border: '1px solid var(--line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className={isDone ? '' : error ? '' : 'spin'}>
                {error ? <Icon.AlertTriangle size={22} style={{ color: 'var(--sem-danger)' }} />
                  : isDone ? <Icon.CheckCircle size={22} style={{ color: 'var(--sem-success)' }} />
                  : <Icon.Sparkles size={22} style={{ color: 'var(--primary)' }} />}
              </span>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="page-eyebrow" style={{ margin: 0, marginBottom: 4 }}>ANÁLISIS INTELIGENTE</div>
              <div style={{ fontSize: 16, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
            </div>
          </div>

          {/* Barra de progreso global con % */}
          {!error && (
            <div style={{ marginTop: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={{ fontSize: 11.5, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isDone ? 'Completado' : ANALYSIS_STEPS[activeIdx]?.label || 'Procesando'}
                  <span className="font-mono" style={{ fontSize: 11, color: 'var(--text-4)' }}>· {fmtElapsed(elapsed)}</span>
                </span>
                <span className="font-display-heavy" style={{ fontSize: 16, color: isDone ? 'var(--sem-success)' : 'var(--primary)' }}>{shownPct}%</span>
              </div>
              <div style={{ height: 7, background: 'var(--bg-2)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${shownPct}%`, borderRadius: 999, transition: 'width 320ms ease',
                  background: isDone ? 'var(--sem-success)' : 'linear-gradient(90deg, var(--primary), var(--primary-glow, var(--primary)))',
                }} />
              </div>
            </div>
          )}

          <p style={{ fontSize: 13, color: 'var(--text-3)', margin: '14px 0 0', lineHeight: 1.55 }}>
            {error ? 'El análisis se ha detenido. Revisa el detalle y pulsa Reintentar para relanzarlo (no hace falta volver a subir los documentos).'
              : isDone ? 'Lizzi ha terminado. La ficha ya está disponible.'
              : 'Lizzi está leyendo los pliegos, cruzando los datos y redactando el informe. Puedes minimizar o cerrar esta ventana: el análisis continúa en segundo plano.'}
          </p>
        </div>

        {/* Detalle del error */}
        {error && (
          <div style={{ padding: '18px 30px 4px' }}>
            <div style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px',
              background: 'var(--sem-danger-soft, var(--bg-2))', border: '1px solid var(--sem-danger)',
              borderRadius: 12, color: 'var(--text-1)',
            }}>
              <Icon.AlertTriangle size={16} style={{ color: 'var(--sem-danger)', flexShrink: 0, marginTop: 1 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--sem-danger)', marginBottom: 3 }}>Detalle del error</div>
                <div style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-2)', wordBreak: 'break-word' }}>{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Pasos */}
        {!error && (
        <div style={{ padding: '22px 30px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {ANALYSIS_STEPS.map((s, i) => {
              const Ico = Icon[s.icon];
              const state = error && i >= 0 && i > activeIdx ? 'idle'
                : i < activeIdx || isDone ? 'done'
                : i === activeIdx ? 'active' : 'idle';
              const showCount = s.key === 'uploading' && docInfo.total > 0 && state === 'active';
              return (
                <div key={s.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, opacity: state === 'idle' ? 0.45 : 1, transition: 'opacity 200ms' }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: state === 'done' ? 'var(--sem-success-soft, var(--bg-2))' : state === 'active' ? 'var(--primary-soft)' : 'var(--bg-2)',
                    border: `1px solid ${state === 'active' ? 'var(--primary)' : 'var(--line-2)'}`,
                    color: state === 'done' ? 'var(--sem-success)' : state === 'active' ? 'var(--primary)' : 'var(--text-4)',
                  }}>
                    {state === 'done' ? <Icon.Check size={15} /> : <Ico size={14} className={state === 'active' ? 'pulse' : ''} />}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13.5, color: state === 'idle' ? 'var(--text-4)' : 'var(--text-1)', fontWeight: state === 'active' ? 600 : 400 }}>
                        {s.label}{showCount ? ` (${docInfo.done}/${docInfo.total})` : ''}
                      </span>
                      {state === 'active' && <span className="dot-flashing" style={{ marginLeft: 'auto' }} />}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{s.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* Acciones */}
        <div style={{ padding: '16px 30px', borderTop: '1px solid var(--line-1)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {error ? (
            <>
              <button className="btn btn-ghost" onClick={goToFicha}>Ir a la ficha</button>
              <button className="btn btn-primary" disabled={retrying} onClick={retry}>
                <span className={retrying ? 'spin' : ''} style={{ display: 'inline-flex' }}><Icon.Refresh size={13} /></span>
                {retrying ? ' Reintentando…' : ' Reintentar análisis'}
              </button>
            </>
          ) : isDone ? (
            <>
              <button className="btn btn-ghost" onClick={() => setOpen(false)}>Cerrar</button>
              <button className="btn btn-primary" onClick={openFicha}><Icon.ArrowRight size={13} /> Abrir ficha</button>
            </>
          ) : (
            <>
              <button className="btn btn-ghost" onClick={() => setMinimized(true)}><Icon.Minus size={13} /> Minimizar y seguir trabajando</button>
              <button className="btn btn-outline" onClick={() => setOpen(false)}>Cerrar y seguir trabajando</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------- Notifications Panel ----------
function NotificationsPanel({ open, onClose }) {
  if (!open) return null;
  const items = [
    { icon:'Sparkles', tone:'sem-violet', title:'Lizzie ha terminado el análisis de SANDETEL', sub:'14 documentos generados · hace 2 horas', unread:true },
    { icon:'AlertTriangle', tone:'sem-warning', title:'ICE Castilla y León cierra en 14 días', sub:'Revisión interna pendiente · hace 3 horas', unread:true },
    { icon:'MessageSquare', tone:'sem-info', title:'Marc te ha mencionado en §3.2', sub:'"¿Repasamos el despliegue territorial?" · hace 5 horas', unread:false },
    { icon:'CheckCircle', tone:'sem-success', title:'Adjudicación confirmada · ESS Andalucía 2025', sub:'87/100 puntos · hace 2 días', unread:false },
  ];
  return (
    <div onClick={onClose} style={{position:'fixed', inset:0, zIndex:90}}>
      <div onClick={e=>e.stopPropagation()} className="scale-in" style={{
        position:'absolute', top:64, right:22, width:380,
        background:'var(--bg-1)', border:'1px solid var(--line-2)', borderRadius:16,
        boxShadow:'var(--shadow-3)', overflow:'hidden', transformOrigin:'top right',
      }}>
        <div style={{padding:'16px 20px', borderBottom:'1px solid var(--line-1)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div style={{fontSize:14, fontWeight:500}}>Notificaciones</div>
          <button className="btn btn-ghost" style={{fontSize:11.5, padding:'4px 8px'}}>Marcar todas como leídas</button>
        </div>
        <div style={{maxHeight:420, overflow:'auto'}}>
          {items.map((it,i)=>{
            const Ico = Icon[it.icon];
            return (
              <div key={i} style={{display:'flex', gap:12, padding:'14px 20px', borderBottom:'1px solid var(--line-1)', cursor:'pointer', background: it.unread ? 'var(--primary-soft)':'transparent'}}>
                <div className={`sem ${it.tone}`} style={{width:32, height:32, padding:0, borderRadius:10, justifyContent:'center', flexShrink:0}}><Ico size={14}/></div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13, color:'var(--text-1)', lineHeight:1.45}}>{it.title}</div>
                  <div style={{fontSize:11.5, color:'var(--text-3)', marginTop:3}}>{it.sub}</div>
                </div>
                {it.unread && <span style={{width:7, height:7, borderRadius:999, background:'var(--primary)', flexShrink:0, marginTop:6}}></span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- Edit Bid Modal (CRUD ficha) ----------
const STATUS_OPTIONS = [
  ['prenotice', 'Anuncio previo'], ['spotted', 'Avistada'], ['analysis', 'Análisis'],
  ['preparation', 'Preparación'], ['submitted', 'Presentada'], ['solvency', 'Solvencia'],
  ['won', 'Adjudicada'], ['lost', 'Perdida'], ['discarded', 'Descartada'],
];

const COMPANY_OPTIONS = ['Lab', 'Madrid', 'Barcelona', 'Málaga'];
const LOT_OPTIONS = Array.from({ length: 12 }, (_, i) => `Lote ${i + 1}`);

function EditBidModal({ open, bid, onClose, onSaved }) {
  const { demoMode } = useData();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !bid) return;
    setForm({
      title: bid.title || '',
      shortTitle: bid.short_title ?? bid.shortTitle ?? '',
      code: bid.code || '',
      lot: bid.lot || '',
      entity: bid.entity || '',
      entityShort: bid.entity_short ?? bid.entityShort ?? '',
      region: bid.region || '',
      company: bid.company || '',
      
      cpv: bid.cpv || '',
      status: bid.status || 'analysis',
      deadline: (bid.deadline || '').slice(0, 10),
      budgetBase: bid.budget_base ?? bid.budgetBase ?? '',
      analysisSourceUrl: bid.analysis_source_url ?? bid.analysisSourceUrl ?? '',
    });
  }, [open, bid]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (!open || !bid) return null;

  const save = async () => {
    if (!form.title?.trim()) { notify('El título no puede estar vacío.', 'AlertTriangle'); return; }
    if (demoMode) { notify('Cambios guardados (modo demo · no se guarda).', 'CheckCircle'); onClose(); return; }
    setSaving(true);
    try {
      const { updateBid } = await import('./data/repo.js');
      await updateBid(bid.id, {
        title: form.title.trim(),
        shortTitle: (form.shortTitle || '').trim() || null,
        code: form.code.trim() || undefined,
        lot: form.lot.trim() || undefined,
        entity: form.entity.trim() || null,
        entityShort: form.entityShort.trim() || null,
        region: form.region.trim() || null,
        company: form.company || null,
        cpv: form.cpv.trim() || null,
        status: form.status,
        deadline: form.deadline || null,
        budgetBase: form.budgetBase === '' ? null : form.budgetBase,
        analysisSourceUrl: form.analysisSourceUrl.trim() || null,
      });
      notify('Licitación actualizada.', 'CheckCircle');
      onClose();
      onSaved && onSaved();
    } catch (e) {
      notify('No se pudo guardar: ' + (e?.message || 'error'), 'AlertTriangle');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{width:620, maxHeight:'90vh', display:'flex', flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:'26px 28px 18px', borderBottom:'1px solid var(--line-1)'}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
            <div>
              <div className="page-eyebrow" style={{margin:0, marginBottom:6}}>EDITAR LICITACIÓN</div>
              <h3 className="font-display-heavy" style={{fontSize:24, margin:0, letterSpacing:'-0.03em'}}>Datos de la licitación</h3>
            </div>
            <button className="btn btn-ghost btn-icon" onClick={onClose}><Icon.X size={15}/></button>
          </div>
        </div>

        <div style={{padding:'22px 28px', display:'flex', flexDirection:'column', gap:16, overflow:'auto'}}>
          <Field label="Título oficial" type="text" value={form.title} onChange={set('title')} placeholder="Título de la licitación"/>
          <Field label="Título resumido" type="text" value={form.shortTitle} onChange={set('shortTitle')} placeholder="Lo genera Lizzi automáticamente; puedes ajustarlo"/>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
            <Field label="Expediente" type="text" value={form.code} onChange={set('code')} placeholder="EXPT…"/>
            <Field label="Lote" type="text" value={form.lot} onChange={set('lot')} placeholder="Lote único"/>
          </div>
          <Field label="Entidad" type="text" value={form.entity} onChange={set('entity')} placeholder="Órgano de contratación"/>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
            <Field label="Entidad (corto)" type="text" value={form.entityShort} onChange={set('entityShort')} placeholder="Siglas"/>
            <Field label="Región (CCAA)" type="text" value={form.region} onChange={set('region')} placeholder="Comunidad Autónoma"/>
          </div>
          <label style={{display:'flex', flexDirection:'column', gap:8}}>
            <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.08em', textTransform:'uppercase', fontWeight:500}}>Compañía</span>
            <select value={form.company} onChange={set('company')} style={{padding:'12px 14px', border:'1px solid var(--line-2)', background:'var(--bg-1)', borderRadius:10, fontSize:14.5, color:'var(--text-1)'}}>
              <option value="">—</option>
              {COMPANY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>

            <Field label="CPV" type="text" value={form.cpv} onChange={set('cpv')} placeholder="Código CPV"/>
            <label style={{display:'flex', flexDirection:'column', gap:8}}>
              <span style={{fontSize:11.5, color:'var(--text-3)', letterSpacing:'0.08em', textTransform:'uppercase', fontWeight:500}}>Estado</span>
              <select value={form.status} onChange={set('status')} style={{padding:'12px 14px', border:'1px solid var(--line-2)', background:'var(--bg-1)', borderRadius:10, fontSize:14.5, color:'var(--text-1)'}}>
                {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14}}>
            <Field label="Plazo de presentación" type="date" value={form.deadline} onChange={set('deadline')}/>
            <Field label="Presupuesto base (€)" type="number" value={form.budgetBase} onChange={set('budgetBase')} placeholder="0"/>
          </div>
          <Field label="Web / enlace" type="text" value={form.analysisSourceUrl} onChange={set('analysisSourceUrl')} placeholder="https://…" icon="Globe"/>
        </div>

        <div style={{padding:'16px 28px', borderTop:'1px solid var(--line-1)', display:'flex', justifyContent:'flex-end', gap:10}}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}><Icon.Check size={14}/> {saving ? 'Guardando…' : 'Guardar cambios'}</button>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { notify, NewBidModal, UploadModal, NotificationsPanel, AnalysisProgressOverlay, EditBidModal });

// =====================================================
// Exports
// =====================================================
export { notify, NewBidModal, UploadModal, NotificationsPanel, AnalysisProgressOverlay, EditBidModal };
